// lib/apiClient.ts
import {
  ApiError,
  NetworkError,
  ValidationError,
} from "@/lib/errors/apiErrors";
import { useAuthStore } from "@/stores/useAuthStore";
import { getAccessToken, clearAuthCookie } from "@/lib/auth/token";
import { env } from "@/config/env";

interface RequestOptions extends RequestInit {
  responseType?: "json" | "text" | "blob";
}

async function handleApiResponse<T>(
  response: Response,
  options?: { responseType?: "json" | "text" | "blob" }
): Promise<T> {
  if (!response.ok) {
    let errorBody: any;

    try {
      errorBody = await response.json();
    } catch {
      errorBody = {
        message: `Erreur HTTP ${response.status}: ${response.statusText}`,
      };
    }

    if (response.status === 401) {
      console.warn("API returned 401 Unauthorized");

      if (typeof window !== "undefined") {
        useAuthStore.getState().logout();
        clearAuthCookie();
        window.location.href = "/login?sessionExpired=true";
      }

      throw new ApiError(
        "Votre session a expiré. Veuillez vous reconnecter.",
        401,
        {}
      );
    }

    if (response.status === 400) {
      let fieldValidationErrors: Record<string, string[]> | undefined;

      if (errorBody.fieldErrors && typeof errorBody.fieldErrors === "object") {
        fieldValidationErrors = Object.entries(errorBody.fieldErrors).reduce(
          (acc, [key, value]) => {
            acc[key] = Array.isArray(value) ? value : [String(value)];
            return acc;
          },
          {} as Record<string, string[]>
        );
      } else if (
        Array.isArray(errorBody.errors) &&
        errorBody.errors.every((e: any) => e.field && e.defaultMessage)
      ) {
        fieldValidationErrors = errorBody.errors.reduce(
          (
            acc: Record<string, string[]>,
            err: { field: string; defaultMessage: string }
          ) => {
            acc[err.field] = acc[err.field]
              ? [...acc[err.field], err.defaultMessage]
              : [err.defaultMessage];
            return acc;
          },
          {}
        );
      }

      if (fieldValidationErrors) {
        throw new ValidationError(
          errorBody.message || "Des erreurs de validation sont survenues.",
          fieldValidationErrors,
          response.status,
          errorBody
        );
      }
    }

    throw new ApiError(
      errorBody.message ||
        `Une erreur est survenue (status: ${response.status})`,
      response.status,
      errorBody
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (options?.responseType === "blob") {
    return (await response.blob()) as unknown as T;
  }

  if (options?.responseType === "text") {
    return (await response.text()) as unknown as T;
  }

  const responseText = await response.text();

  if (!responseText) {
    return undefined as T;
  }

  try {
    return JSON.parse(responseText) as T;
  } catch (error) {
    console.error("Échec du parsing JSON:", responseText, error);
    throw new ApiError("Réponse invalide du serveur.", response.status, {
      responseBody: responseText,
    });
  }
}

export async function apiClient<T>(
  endpointSuffix: string,
  options: RequestOptions = {}
): Promise<T> {
  const { headers, body, responseType, ...restOptions } = options;
  const completeUrl = `${env.apiBaseUrl}${endpointSuffix}`;

  const defaultHeaders: HeadersInit = { ...headers };

  if (!(body instanceof FormData) && responseType !== "blob") {
    (defaultHeaders as Record<string, string>)["Content-Type"] =
      "application/json";
  }

  let token: string | null = null;
  if (typeof window !== "undefined") {
    token = getAccessToken();
  } else {
    try {
      const { cookies } = await import("next/headers");
      const cookieStore = await cookies();
      token = cookieStore.get("token")?.value ?? null;
    } catch {
      // hors contexte Next.js (ex: génération statique sans requête)
    }
  }
  if (token) {
    (defaultHeaders as Record<string, string>)["Authorization"] =
      `Bearer ${token}`;
  }

  try {
    const response = await fetch(completeUrl, {
      ...restOptions,
      headers: defaultHeaders,
      body,
    });

    return handleApiResponse<T>(response, { responseType });
  } catch (error) {
    if (
      error instanceof ApiError ||
      error instanceof ValidationError ||
      error instanceof NetworkError
    ) {
      throw error;
    }

    console.error("Erreur réseau ou fetch dans apiClient:", error);
    throw new NetworkError(
      "Impossible de joindre le serveur. Vérifiez votre connexion internet."
    );
  }
}